import { toast } from 'react-toastify'
import {
  addOrUpdateGenericDrugsInIdb,
  findGenericDrugInIdbByMedKey,
  getGenericDrugsByIds,
} from 'src/emr/database/indexedDbDao'
import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
  sleep,
} from 'src/mentor/helpers/utilityFunctions'
import { getMPDataWithParams, postMPDataWithParams } from 'src/mentor/services'

export function keepSectionStateBeforeClearAll(clearedSectionName, data) {
  const stringifiedData = JSON.stringify(data)
  const sessionData = JSON.parse(sessionStorage.getItem('sectionStates') || '{}')
  sessionData[clearedSectionName] = stringifiedData
  sessionStorage.setItem('sectionStates', JSON.stringify(sessionData))
}

export function undoClearAllChangeInPrescription(
  clearedSectionName,
  callbackFunctionForRecoveredData,
) {
  const sessionData = JSON.parse(sessionStorage.getItem('sectionStates') || '{}')
  if (sessionData?.[clearedSectionName]) {
    const recoveredData = JSON.parse(sessionData[clearedSectionName])
    delete sessionData[clearedSectionName]
    sessionStorage.setItem('sectionStates', JSON.stringify(sessionData))
    if (callbackFunctionForRecoveredData) {
      callbackFunctionForRecoveredData(recoveredData)
    }
  } else {
    console.log('No data available to undo.')
  }
}

export async function getGenericDrugDataForDrug(genericNameId) {
  // find in idb, else find on server, save in idb, and ultimately return
  console.log('trying to find for ')
  console.log(genericNameId)
  const foundGenDrug = await findGenericDrugInIdbByMedKey(genericNameId)
  console.log(foundGenDrug)
  if (foundGenDrug) {
    return foundGenDrug
  } else {
    helperOfGetGenericDrugDataForDrug(genericNameId)
    return null
  }
}
export async function getGenericDrugDataForDrugByFormulaId(genericFormulaId, genericFormulaTitle) {
  // find in idb, else find on server, save in idb, and ultimately return
  console.log('trying to find for ')
  console.log(genericFormulaId)
  if (!genericFormulaId) {
    return null
  }
  const foundGenDrugs = await getGenericDrugsByIds([genericFormulaId])
  let foundGenDrug = foundGenDrugs?.[0]

  //TODO: IF THE FOUND ITEM IS NOT FOUND, THEN JUST FIND BASED ON TITLE AND IF ITS TITLE IS SAME, KEEP IT.
  if (foundGenDrug) {
    return foundGenDrug
  } else {
    return await helperOfGetGenericDrugDataForDrug(undefined, genericFormulaId)
    // return null
  }
}

async function helperOfGetGenericDrugDataForDrug(genericNameId, genericFormulaId) {
  const response = await getMPDataWithParams(`/api/secure/clinic-practitioner/drug-generic-formula/list`, {
    medKey: genericNameId,
    genericFormulaId: genericFormulaId,
  })
  if (response?.status === 200 && response?.data && Array.isArray(response?.data)) {
    addOrUpdateGenericDrugsInIdb(response?.data)
    return response?.data
  }
}

const isValidDosage = (dosage) => {
  const dosageTitle = dosage?.title
  if (!dosageTitle) return false
  if (typeof dosageTitle !== 'string') return false

  const parts = dosageTitle.split('-')

  return parts.some((part) => part !== '0') // Valid if at least one part is not "0"
}

export const filterValidDosages = (dosageArray = []) => {
  return dosageArray?.filter(isValidDosage)
}

export async function generatePrescriptionPDF({
  bookedEventId,
  sendImmediately = false,
  sendPrescriptionPdfApiSignature = `/api/secure/doctor/prescription/generate/pdf`,
}) {
  const response = await postMPDataWithParams(sendPrescriptionPdfApiSignature, null, {
    bookedEventId,
    'send-immediately': sendImmediately,
  })
  if (response?.status === 200) {
    if (isCommonResponseSuccessfulV2(response)) {
      const respData = getDataMapFromCommonResponse(response)
      return respData
    } else {
      toast.error('Could not generate prescription pdf')
      console.error('error in generating prescription pdf', response?.data?.message)
    }
  } else {
    toast.error('Could not generate prescription pdf')
    console.error('error in generating prescription pdf', response?.message)
  }
}

export async function downloadPDFFromURL(url, filename = 'prescription.pdf', maxRetries = 3, retryDelay = 2000, returnBlob = false) {
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      // Fetch without credentials since media server doesn't support CORS credentials
      // Media files should be accessible via signed URLs or public access
      const response = await fetch(url, {
        mode: 'cors', // Allow CORS requests
      })

      if (!response.ok) {
        // If 403 or 404, the PDF might not be ready yet - retry
        if ((response.status === 403 || response.status === 404) && attempt < maxRetries) {
          const delay = retryDelay * attempt // Exponential backoff: 2s, 4s, 6s
          console.log(`PDF not ready yet (${response.status}). Retrying in ${delay}ms... (Attempt ${attempt}/${maxRetries})`)
          toast.info(`PDF is being generated... Retrying in ${delay / 1000}s (${attempt}/${maxRetries})`, {
            autoClose: delay,
          })
          await sleep(delay)
          continue
        }
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const blob = await response.blob()

      // If returnBlob is true, return the blob instead of downloading
      if (returnBlob) {
        if (attempt > 1) {
          toast.success('PDF loaded successfully!', { autoClose: 2000 })
        }
        return { success: true, blob, filename }
      }

      // Create download link
      const downloadUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = downloadUrl
      link.download = filename

      // Trigger download
      document.body.appendChild(link)
      link.click()

      // Cleanup
      window.URL.revokeObjectURL(downloadUrl)
      document.body.removeChild(link)

      if (attempt > 1) {
        toast.success('PDF downloaded successfully!', { autoClose: 2000 })
      }
      return { success: true, blob, filename }
    } catch (error) {
      // If this is the last attempt, show error
      if (attempt === maxRetries) {
        console.error('Error downloading PDF after all retries:', error)
        if (error.message?.includes('403') || error.message?.includes('404')) {
          toast.error('PDF is not available yet. Please try again in a few moments.', {
            autoClose: 5000,
          })
        } else {
          toast.error('Failed to download PDF. Please try again.', {
            autoClose: 5000,
          })
        }
        return { success: false, error }
      }
      
      // For network errors, retry with exponential backoff
      const delay = retryDelay * attempt
      console.log(`Download failed. Retrying in ${delay}ms... (Attempt ${attempt}/${maxRetries})`)
      toast.info(`Retrying download... (${attempt}/${maxRetries})`, {
        autoClose: delay,
      })
      await sleep(delay)
    }
  }
  
  return { success: false }
}
